import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { getLegalDocs, acceptLegal, acceptSpecialConsent } from "@/lib/api";
import { toast } from "sonner";
import { Heart, Shield, FileText, Loader2, ShieldCheck } from "lucide-react";

export default function AcceptLegal() {
  const { user, refresh, logout } = useAuth();
  const navigate = useNavigate();

  const [docs, setDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [specialConsentChecked, setSpecialConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeDoc, setActiveDoc] = useState("privacy");

  useEffect(() => {
    getLegalDocs()
      .then(setDocs)
      .catch(() =>
        toast.error("Could not load legal documents. Check your connection."),
      )
      .finally(() => setLoading(false));
  }, []);

  if (!user) {
    navigate("/");
    return null;
  }

  const canSubmit =
    privacyChecked &&
    termsChecked &&
    specialConsentChecked &&
    !submitting &&
    !!docs;

  const handleAccept = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // 1. Legal acceptance (must succeed — this is the whole point).
      await acceptLegal(docs.privacy.version, docs.terms.version);

      // 2. Special-info consent — non-fatal.
      //    Only call if the user hasn't already consented AND we have a
      //    version on file. Skip entirely for returning users who already
      //    consented in a prior session — calling it unconditionally with
      //    a missing/undefined version 400s and used to leave users stuck
      //    on this screen forever.
      if (!user?.special_info_consent && user?.current_consent_version) {
        try {
          await acceptSpecialConsent(user.current_consent_version);
        } catch (e) {
          console.warn("Special consent recording failed (non-fatal):", e);
        }
      }

      const updated = await refresh();

      toast.success("Thank you — you're all set.");

      // 3. Route based on onboarding state.
      //    CRITICAL: only force setup when onboarding_complete is EXPLICITLY
      //    false. Treat null/undefined as "already onboarded" so returning
      //    users who bumped the legal version don't get sent back through
      //    profile setup.
      if (updated?.onboarding_complete === false) {
        navigate("/profile/setup", { replace: true });
      } else {
        navigate("/find", { replace: true });
      }
    } catch (err) {
      toast.error(
        err.response?.data?.detail ||
          "Could not record your acceptance. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecline = async () => {
    if (
      !window.confirm(
        "Without accepting, you cannot use Haven. Your session will be closed. Continue?",
      )
    )
      return;
    try {
      await logout();
      navigate("/");
    } catch {
      navigate("/");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const currentDoc = docs?.[activeDoc];

  return (
    <div className="min-h-screen bg-[#FAFAF7] py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-[#0F0F10] text-white rounded-xl flex items-center justify-center mx-auto mb-3">
            <Heart className="w-6 h-6" fill="currentColor" />
          </div>
          <h1 className="font-display text-2xl font-semibold">
            Before you continue
          </h1>
          <p className="text-sm text-[#6B6B70] mt-1">
            We've updated our Privacy Policy. Please review and accept the
            current version.
          </p>
        </div>

        <div className="bg-white border border-[#E7E5E0] rounded-2xl overflow-hidden">
          <div className="flex border-b border-[#E7E5E0]">
            <button
              onClick={() => setActiveDoc("privacy")}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                activeDoc === "privacy"
                  ? "bg-purple-50 text-purple-700 border-b-2 border-purple-600"
                  : "text-[#6B6B70] hover:bg-gray-50"
              }`}
            >
              <Shield className="w-4 h-4" /> Privacy Policy
            </button>
            <button
              onClick={() => setActiveDoc("terms")}
              className={`flex-1 py-3 text-sm font-medium flex items-center justify-center gap-2 ${
                activeDoc === "terms"
                  ? "bg-purple-50 text-purple-700 border-b-2 border-purple-600"
                  : "text-[#6B6B70] hover:bg-gray-50"
              }`}
            >
              <FileText className="w-4 h-4" /> Terms of Use
            </button>
          </div>

          <div className="p-6 max-h-[380px] overflow-y-auto">
            <p className="text-xs text-[#6B6B70] mb-3">
              Version {currentDoc?.version} · Effective{" "}
              {currentDoc?.effective_date}
            </p>
            <pre className="whitespace-pre-wrap font-sans text-sm text-[#404040] leading-relaxed">
              {currentDoc?.text}
            </pre>
          </div>

          <div className="p-6 border-t border-[#E7E5E0] bg-[#FAFAF7] space-y-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={privacyChecked}
                onChange={(e) => setPrivacyChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-purple-600"
              />
              <span className="text-sm text-[#404040]">
                I have read and agree to the <strong>Privacy Policy</strong>{" "}
                (v{docs?.privacy.version}).
              </span>
            </label>

            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-purple-600"
              />
              <span className="text-sm text-[#404040]">
                I have read and agree to the <strong>Terms of Use</strong>{" "}
                (v{docs?.terms.version}) and confirm I am 18 or older.
              </span>
            </label>

            <div className="p-3 border-2 border-purple-200 bg-purple-50/40 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={specialConsentChecked}
                  onChange={(e) => setSpecialConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-purple-600"
                />
                <span className="text-sm text-[#404040]">
                  <strong className="text-purple-800">
                    Required for Haven's community:
                  </strong>{" "}
                  I consent to Haven processing my health status (HIV, HPV,
                  HSV, or other STI) so I can participate in Haven's matching
                  community. This is special personal information under POPIA
                  sections 26–27. It is shown only to matches I approve —
                  never publicly, never to advertisers. I can withdraw this
                  at any time by clearing my health status in my profile.
                </span>
              </label>
            </div>

            <button
              onClick={handleAccept}
              disabled={!canSubmit}
              className="w-full py-3 rounded-xl bg-purple-600 text-white font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving…
                </>
              ) : (
                <>
                  <ShieldCheck className="w-4 h-4" /> Accept all and continue
                </>
              )}
            </button>

            <p className="text-xs text-center text-[#6B6B70]">
              Haven is strictly for adults 18 and over. If you do not agree to
              these terms,{" "}
              <button
                onClick={handleDecline}
                className="underline hover:text-[#0F0F10]"
              >
                sign out
              </button>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}